import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import { Accordion, AccordionContent, AccordionTitle, Grid, Table } from "semantic-ui-react";
import {
  AccordionTitleView,
  CommonTable,
  CustomButton,
  DropDown,
  InputText
} from "../../../components";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import { InputNumber } from "../../../components/InputText/InputNumber";
import { PER_TYPES, PER_WEIGHT_UNITS, SipCol } from "../../../config/constants";
import { AccordionTypes } from "../../../helpers/AccordionHelper";

interface SimulationSipContentProps {
  getSipData: (data: any) => void;
  processCIPData: any;
  chemicalDataType: any;
  currentCipSipData: any;
  originalCipSipData: any;
  originalProcessCIPData: any;
  handleCIPValidation: () => boolean;
  currentAccordionStatus: (activeStatus: boolean, requestType: AccordionTypes) => boolean;
}

const SimulationSipContentContent = ({
  processCIPData,
  chemicalDataType,
  getSipData,
  currentCipSipData,
  originalCipSipData,
  originalProcessCIPData,
  handleCIPValidation = () => true,
  currentAccordionStatus
}: SimulationSipContentProps) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [defaultPerTypeFrequency, setDefaultPerTypeFrequency] = useState("week");
  const [defaultPerTypePA, setDefaultPerTypePA] = useState("week");
  const [defaultWeightUnit, setDefaultWeightUnit] = useState("kg");
  const [defaultChemicalType, setDefaultChemicalType] = useState<any>("");
  const [filteredChemicals, setFilteredChemicals] = useState(chemicalDataType || []);
  const [chemicalData, setChemicalData] = useState<any>([]);
  const [enableEdit, setEnableEdit] = useState(false);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [enableUpdateButton, setEnableUpdateButton] = useState<any>(false);

  const values: any = useMemo(() => {
    return {
      cipData: originalProcessCIPData || [],
      frequency: currentCipSipData?.[0]?.frequency?.amount || 0,
      prodAmount: currentCipSipData?.[0]?.productionAmount?.amount || 0,
      energyConsumption: currentCipSipData?.[0]?.energyConsumption || 0,
      manufacturer: currentCipSipData?.[0]?.manufacturer || "",
      modelNumber: currentCipSipData?.[0]?.modelNumber || "",
      steamFlow: currentCipSipData?.[0]?.steamFlow || 0,
      steamPressure: currentCipSipData?.[0]?.steamPressure || 0,
      wasteWater: currentCipSipData?.[0]?.wasteWater || 0,
      waterConsumption: currentCipSipData?.[0]?.waterConsumption || 0,
      concentration: 0,
      consumption: 0
    };
  }, [accordionEnabled, currentCipSipData, originalProcessCIPData]);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
    control
  } = useForm({
    mode: "all",
    defaultValues: values,
    values: values
  });

  const { fields: cipDetails } = useFieldArray({
    control,
    name: "cipData"
  });

  // Update dropdown data and chemical data when chemicalDataType changes
  useEffect(() => {
    if (chemicalDataType && chemicalDataType.length > 0) {
      setFilteredChemicals(chemicalDataType);
      setDefaultChemicalType(chemicalDataType[0]?.value || "");
    }
    if (currentCipSipData?.[0]?.chemical) {
      setChemicalData(currentCipSipData[0].chemical);
    }
    if (currentCipSipData?.[0]?.frequency?.per) {
      setDefaultPerTypeFrequency(currentCipSipData[0].frequency.per);
    }
    if (currentCipSipData?.[0]?.productionAmount?.per) {
      setDefaultPerTypePA(currentCipSipData[0].productionAmount.per);
    }
    if (currentCipSipData?.[0]?.productionAmount?.perUnit) {
      setDefaultWeightUnit(currentCipSipData[0].productionAmount.perUnit);
    }
  }, [chemicalDataType, currentCipSipData]);

  const onSubmit = (data: any) => {
    data.frequency = {
      amount: data?.frequency || 0,
      per: defaultPerTypeFrequency
    };
    data.productionAmount = {
      amount: data?.prodAmount || 0,
      per: defaultPerTypePA,
      perUnit: defaultWeightUnit
    };
    data.chemical = chemicalData;
    delete data.cipData;
    delete data.concentration;
    delete data.consumption;

    getSipData([data]);
    currentAccordionStatus(false, AccordionTypes.CIP_SIP);
    setAccordionEnabled(false);
  };

  const cancelHandler = () => {
    currentAccordionStatus(false, AccordionTypes.CIP_SIP);
    setAccordionEnabled(false);
    cipDetails.forEach((_: any, index: any) => {
      setValue(`cipData.${index}.value`, null);
    });
  };

  const handleClick = () => {
    if (!handleCIPValidation()) return;
    const status = currentAccordionStatus(!accordionEnabled, AccordionTypes.CIP_SIP);
    setAccordionEnabled(status);
  };

  const handleDeleteCancel = () => {
    setVisibleDeleteModal(false);
  };

  const handleDeleteApprove = () => {
    const updatedChemicalData = chemicalData.filter(
      (_: any, index: number) => index !== deleteIndex
    );
    setChemicalData(updatedChemicalData);
    setVisibleDeleteModal(false);
    setDeleteIndex(null);
  };

  const addNewChemical = () => {
    const createNewChemicalData = () => ({
      chemicalName:
        chemicalDataType.find((item: any) => item.value === defaultChemicalType)?.text || "",
      concentration: getValues("concentration"),
      consumption: getValues("consumption")
    });

    const newChemicalData = createNewChemicalData();

    if (enableEdit && deleteIndex !== null) {
      const updatedChemicalData = chemicalData.map((item: any, index: number) =>
        index === deleteIndex ? { ...item, ...newChemicalData } : item
      );
      setChemicalData(updatedChemicalData);
      setEnableEdit(false);
      setDeleteIndex(null);
    } else {
      setChemicalData((prevData: any) => [...prevData, newChemicalData]);
    }

    setValue("concentration", 0);
    setValue("consumption", 0);
    setDefaultChemicalType(chemicalDataType[0]?.value || "");
  };

  const onEditHandler = (item: any, index: number) => {
    setEnableEdit(true);
    setDeleteIndex(index);
    setValue("concentration", item.concentration);
    setValue("consumption", item.consumption);
    setDefaultChemicalType(
      chemicalDataType.find((data: any) => data.text === item.chemicalName)?.value || ""
    );
  };
  return (
    <Accordion>
      <AccordionTitle active={accordionEnabled} onClick={handleClick}>
        <AccordionTitleView accordionEnabled={accordionEnabled} title={"CIP / SIP"} />
      </AccordionTitle>
      <AccordionContent active={accordionEnabled}>
        <Grid>
          <Grid.Row divided>
            <Grid.Column computer={8} tablet={8} mobile={8}>
              <Grid>
                <Grid.Column computer={8} tablet={16} mobile={16} className="paddingRemoveBottom">
                  <InputNumber
                    labelName={"Frequency"}
                    checkValidation={true}
                    transform={{
                      input: (value: any) =>
                        isNaN(value) || /^\d*\.\d+$/.test(value) ? "" : value.toString(),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={originalCipSipData?.[0]?.frequency?.amount || "0"}
                    name={`originalFrequency`}
                    disabled={true}
                  />
                </Grid.Column>
                <Grid.Column
                  computer={8}
                  tablet={16}
                  mobile={16}
                  className="tw-pt-0 customMarginTop paddingRemoveBottom"
                >
                  <DropDown
                    labelName={"Time(s) per"}
                    customLabelText={"customLabelText"}
                    currentData={PER_TYPES}
                    defaultValue={originalCipSipData?.[0]?.frequency?.per || "week"}
                    customGridColumn={"customGridColomnType"}
                    handleChangeState={() => {}}
                    disabled={true}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="paddingRemoveBottom">
                  <InputNumber
                    labelName={"Production Amount"}
                    transform={{
                      input: (value: any) => (isNaN(value) ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={originalCipSipData?.[0]?.productionAmount?.amount || "0"}
                    name={`originalProdAmount`}
                    disabled={true}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={16} mobile={16} className="tw-pt-0 customMarginTop">
                  <DropDown
                    labelName={"Unit"}
                    customLabelText={""}
                    currentData={PER_WEIGHT_UNITS}
                    defaultValue={originalCipSipData?.[0]?.productionAmount?.perUnit || "kg"}
                    customGridColumn={"customGridColomnType"}
                    handleChangeState={() => {}}
                    disabled={true}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={16} mobile={16} className="tw-pt-0 customMarginTop">
                  <DropDown
                    labelName={"Per"}
                    customLabelText={""}
                    currentData={PER_TYPES}
                    defaultValue={originalCipSipData?.[0]?.productionAmount?.per || "week"}
                    customGridColumn={"customGridColomnType"}
                    handleChangeState={() => {}}
                    disabled={true}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="tw-pt-0">
                  <InputText
                    labelName={"Manufacturer"}
                    register={register}
                    placeholder={originalCipSipData?.[0]?.manufacturer}
                    name="originalManufacturer"
                    disabled={true}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="tw-pt-0">
                  <InputText
                    labelName={"Model Number"}
                    register={register}
                    placeholder={originalCipSipData?.[0]?.modelNumber}
                    name="originalModelNumber"
                    disabled={true}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="tw-pt-0">
                  <InputNumber
                    labelName={"Energy Consumption"}
                    transform={{
                      input: (value: any) => (isNaN(value) || value === 0 ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={originalCipSipData?.[0]?.energyConsumption || "0"}
                    name={`originalEnergyConsumption`}
                    rightContentEnable={true}
                    rightContent={"kWh"}
                    disabled={true}
                    className="tw-disabled-input"
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={16} mobile={16} className="tw-pt-0">
                  <InputNumber
                    labelName={"Steam Flow"}
                    transform={{
                      input: (value: any) => (isNaN(value) || value === 0 ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={originalCipSipData?.[0]?.steamFlow || "0"}
                    name={`originalSteamFlow`}
                    rightContentEnable={true}
                    rightContent={"kg/hr"}
                    disabled={true}
                    className="tw-disabled-input"
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={16} mobile={16} className="tw-pt-0">
                  <InputNumber
                    labelName={"Steam Pressure"}
                    transform={{
                      input: (value: any) => (isNaN(value) || value === 0 ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={originalCipSipData?.[0]?.steamPressure || "0"}
                    name={`originalSteamPressure`}
                    rightContentEnable={true}
                    rightContent={"bar"}
                    disabled={true}
                    className="tw-disabled-input"
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="tw-pt-0">
                  <InputNumber
                    labelName={"Water Consumption"}
                    transform={{
                      input: (value: any) => (isNaN(value) || value === 0 ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={originalCipSipData?.[0]?.waterConsumption || "0"}
                    name={`originalWaterConsumption`}
                    rightContentEnable={true}
                    rightContent={"L"}
                    disabled={true}
                    className="tw-disabled-input"
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="tw-pt-0">
                  <InputNumber
                    labelName={"Waste Water"}
                    transform={{
                      input: (value: any) => (isNaN(value) || value === 0 ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={originalCipSipData?.[0]?.wasteWater || "0"}
                    name={`originalWasteWater`}
                    rightContentEnable={true}
                    rightContent={"L"}
                    disabled={true}
                    className="tw-disabled-input"
                  />
                </Grid.Column>
                {originalCipSipData?.[0]?.chemical?.length > 0 && (
                  <Grid.Column
                    computer={16}
                    tablet={16}
                    mobile={16}
                    className="pesticideTable tw-mt-2 tw-mb-2"
                  >
                    <CommonTable tableHeaderData={SipCol} className="pesticideTable">
                      {originalCipSipData?.[0]?.chemical?.map((item: any, index: any) => (
                        <Table.Row key={`chemical_${index}`}>
                          <Table.Cell>
                            <p>{item?.chemicalName ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.concentration ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.consumption ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell></Table.Cell>
                        </Table.Row>
                      ))}
                    </CommonTable>
                  </Grid.Column>
                )}
              </Grid>
            </Grid.Column>
            <Grid.Column computer={8} tablet={8} mobile={8}>
              <Grid>
                <Grid.Column computer={8} tablet={16} mobile={16} className="paddingRemoveBottom">
                  <InputNumber
                    labelName={"Frequency"}
                    checkValidation={true}
                    transform={{
                      input: (value: any) =>
                        isNaN(value) || /^\d*\.\d+$/.test(value) ? "" : value.toString(),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={"0"}
                    name={`frequency`}
                  />
                </Grid.Column>
                <Grid.Column
                  computer={8}
                  tablet={16}
                  mobile={16}
                  className="tw-pt-0 customMarginTop paddingRemoveBottom"
                >
                  <DropDown
                    labelName={"Time(s) per"}
                    customLabelText={"customLabelText"}
                    currentData={PER_TYPES}
                    defaultValue={defaultPerTypeFrequency}
                    customGridColumn={"customGridColomnType"}
                    handleChangeState={(e: any, { value }: any) => {
                      setDefaultPerTypeFrequency(value);
                    }}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="paddingRemoveBottom">
                  <InputNumber
                    labelName={"Production Amount"}
                    transform={{
                      input: (value: any) => (isNaN(value) ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={"0"}
                    name={`prodAmount`}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={16} mobile={16} className="tw-pt-0 customMarginTop">
                  <DropDown
                    labelName={"Unit"}
                    customLabelText={""}
                    currentData={PER_WEIGHT_UNITS}
                    defaultValue={defaultWeightUnit}
                    customGridColumn={"customGridColomnType"}
                    handleChangeState={(e: any, { value }: any) => {
                      setDefaultWeightUnit(value);
                    }}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={16} mobile={16} className="tw-pt-0 customMarginTop">
                  <DropDown
                    labelName={"Per"}
                    customLabelText={""}
                    currentData={PER_TYPES}
                    defaultValue={defaultPerTypePA}
                    customGridColumn={"customGridColomnType"}
                    handleChangeState={(e: any, { value }: any) => {
                      setDefaultPerTypePA(value);
                    }}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="tw-pt-0">
                  <InputText
                    labelName={"Manufacturer"}
                    register={register}
                    placeholder="Manufacturer"
                    name="manufacturer"
                    errorMessage={"Please enter manufacturer"}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="tw-pt-0">
                  <InputText
                    labelName={"Model Number"}
                    register={register}
                    placeholder="Model Number"
                    name="modelNumber"
                    errorMessage={"Please enter model number"}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="tw-pt-0">
                  <InputNumber
                    labelName={"Energy Consumption"}
                    transform={{
                      input: (value: any) => (isNaN(value) || value === 0 ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={"0"}
                    name={`energyConsumption`}
                    rightContentEnable={true}
                    rightContent={"kWh"}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={16} mobile={16} className="tw-pt-0">
                  <InputNumber
                    labelName={"Steam Flow"}
                    transform={{
                      input: (value: any) => (isNaN(value) || value === 0 ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={"0"}
                    name={`steamFlow`}
                    rightContentEnable={true}
                    rightContent={"kg/hr"}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={16} mobile={16} className="tw-pt-0">
                  <InputNumber
                    labelName={"Steam Pressure"}
                    transform={{
                      input: (value: any) => (isNaN(value) || value === 0 ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={"0"}
                    name={`steamPressure`}
                    rightContentEnable={true}
                    rightContent={"bar"}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="tw-pt-0">
                  <InputNumber
                    labelName={"Water Consumption"}
                    transform={{
                      input: (value: any) => (isNaN(value) || value === 0 ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={"0"}
                    name={`waterConsumption`}
                    rightContentEnable={true}
                    rightContent={"L"}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={16} mobile={16} className="tw-pt-0">
                  <InputNumber
                    labelName={"Waste Water"}
                    transform={{
                      input: (value: any) => (isNaN(value) || value === 0 ? "" : value.toString()),
                      output: (e: any) => {
                        return e;
                      }
                    }}
                    control={control}
                    placeholder={"0"}
                    name={`wasteWater`}
                    rightContentEnable={true}
                    rightContent={"L"}
                  />
                </Grid.Column>
                {chemicalData.length > 0 && (
                  <Grid.Column
                    computer={16}
                    tablet={16}
                    mobile={16}
                    className="pesticideTable tw-mt-2 tw-mb-2"
                  >
                    <CommonTable tableHeaderData={SipCol} className="pesticideTable">
                      {chemicalData.map((item: any, index: any) => (
                        <Table.Row key={`chemical_${index}`}>
                          <Table.Cell>
                            <p>{item?.chemicalName ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.concentration ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.consumption ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <Grid>
                              <Grid.Column computer={8} tablet={16} mobile={16}>
                                <MdModeEditOutline
                                  cursor={"pointer"}
                                  size={24}
                                  color="var(--tableEditIcon)"
                                  onClick={() => {
                                    onEditHandler(item, index);
                                    setEnableUpdateButton(true);
                                  }}
                                />
                              </Grid.Column>
                              <Grid.Column computer={8} tablet={16} mobile={16}>
                                <MdDeleteOutline
                                  cursor={"pointer"}
                                  size={24}
                                  color="var(--tableEditIcon)"
                                  onClick={() => {
                                    setVisibleDeleteModal(true);
                                    setDeleteIndex(index);
                                  }}
                                />
                              </Grid.Column>
                            </Grid>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </CommonTable>
                  </Grid.Column>
                )}
                <Grid>
                  <Grid.Column computer={8} tablet={8} mobile={16}>
                    <DropDown
                      required={true}
                      labelName="Chemical Name"
                      placeholder="Select Chemical"
                      currentData={chemicalDataType || []}
                      defaultValue={defaultChemicalType}
                      handleChangeState={(e, data: any) => {
                        setDefaultChemicalType(data?.value);
                      }}
                      onSearchChange={(e, data: any) => {
                        const filteredArray = chemicalDataType?.filter((chemical: any) =>
                          chemical?.text?.toLowerCase().includes(data.searchQuery?.toLowerCase())
                        );
                        setFilteredChemicals(filteredArray || []);
                      }}
                      loading={false}
                      search
                    />
                  </Grid.Column>
                  <Grid.Column computer={3} tablet={6} mobile={16}>
                    <InputNumber
                      transform={{
                        input: (value: any) =>
                          isNaN(value) || value === 0 ? "" : value.toString(),
                        output: (e: any) => {
                          return e;
                        }
                      }}
                      control={control}
                      labelName={"Concentration"}
                      placeholder={"0"}
                      name="concentration"
                    />
                  </Grid.Column>
                  <Grid.Column computer={3} tablet={6} mobile={16}>
                    <InputNumber
                      transform={{
                        input: (value: any) =>
                          isNaN(value) || value === 0 ? "" : value.toString(),
                        output: (e: any) => {
                          return e;
                        }
                      }}
                      control={control}
                      labelName={"Consumption"}
                      placeholder={"0"}
                      name="consumption"
                    />
                  </Grid.Column>
                  <Grid.Column computer={2} tablet={2} mobile={16} className="pesticideTable tw-mb-1">
                    <CustomButton
                      theme="green"
                      title={enableUpdateButton ? "Update" : ""}
                      icon={!enableUpdateButton ? "plus" : null}
                      customColumnStyle={"plusButton"}
                      disabled={!defaultChemicalType}
                      onClick={() => {
                        addNewChemical();
                        setEnableUpdateButton(false);
                      }}
                    />
                  </Grid.Column>
                </Grid>
                <div className="sipButtonContent">
                  <div className="sipCancel">
                    <CustomButton
                      onClick={cancelHandler}
                      title="Cancel"
                      theme="green"
                      buttonOutLine={true}
                    />
                  </div>
                  <div className="sipAdd">
                    <CustomButton onClick={handleSubmit(onSubmit)} title="Save" theme="green" />
                  </div>
                </div>
              </Grid>
            </Grid.Column>
          </Grid.Row>
          <ConfirmModal
            viewModal={visibleDeleteModal}
            closeModal={() => setVisibleDeleteModal(false)}
            cancel={handleDeleteCancel}
            approve={handleDeleteApprove}
            title="Delete Chemical Data"
            subTitle="Are you sure you want to delete chemical data?"
          />
        </Grid>
      </AccordionContent>
    </Accordion>
  );
};
export default SimulationSipContentContent;
